"use client";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  cache,
} from "react";
import { createClient } from "@/lib/supabase/client";
import Gleap from "gleap";
import { Tables } from "../../supabase/functions/_shared/models/database.types";
import { getSignedUrl } from "@/services/file";

const supabase = createClient();

type ProfileContextType = {
  profile: Tables<"profiles"> | null;
  fetchProfile: () => Promise<void>;
};

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [profile, setProfile] = useState<Tables<"profiles"> | null>(null);

  const fetchProfile = async () => {
    const { data: session } = await supabase.auth.getUser();
    let user = session?.user;
    if (!user) {
      throw new Error("User not found");
    }
    const user_id = user?.id;
    if (!user_id) {
      throw new Error("User ID not found");
    }
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", user_id)
      .single();
    if (error) {
      throw new Error("An error occurred while fetching your profile.");
    }
    let signedProfilePictureUrl = null;
    try {
      signedProfilePictureUrl = await getSignedUrl({
        bucketId: "avatar",
        name: `${user_id}.png`,
      });
    } catch (error) {
      console.log("No profile picture has been found:", error);
    }

    setProfile({
      ...data,
      avatar_url: signedProfilePictureUrl?.signedUrl ?? null,
    });
    Gleap.identify(data.id, {
      email: data.email ?? "",
      name: `${data.first_name} ${data.last_name}`,
    });
  };

  useEffect(() => {
    fetchProfile();
    const { data: authListener } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN" || event === "USER_UPDATED") {
        fetchProfile();
      } else if (event === "SIGNED_OUT") {
        setProfile(null);
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  return (
    <ProfileContext.Provider value={{ profile, fetchProfile }}>
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error("useProfile must be used within a ProfileProvider");
  }
  return context;
};
